import type { FoGesrPdfDocument } from './foGesrPdfDocument.ts';
import { ALCALDIA_LOGO_PDF_DATA_URL } from 'alcaldia-logo-pdf-data-url';
import {
  addImageFormatFromDataUrl,
  fitInBox,
  naturalImageSize,
  resolveImageSrcForPdf,
} from './resolveImageForPdf.ts';
/** Escape reg exp fragment. */


function escapeRegExpFragment(s: string): string {
  return s.replaceAll(/[.*+?^${}()|[\]\\]/g, (m) => `\\${m}`);
}

const AVISO_BOLD_FRAGMENTS: readonly string[] = [
  'AVISO DE PRIVACIDAD Y AUTORIZACIÓN PARA EL TRATAMIENTO DE DATOS PERSONALES:',
  'MI AUTORIZACIÓN SE PERFECCIONA',
  '(+57) 6044444144',
  'NIT 890.905.211-1',
  'DISTRITO DE MEDELLÍN',
  'DISTRITO DE MEDELLIN',
];
/** Split aviso by bold fragments. */


function splitAvisoByBoldFragments(paragraph: string): { text: string; bold: boolean }[] {
  const sorted = [...AVISO_BOLD_FRAGMENTS].sort((a, b) => b.length - a.length);
  const pattern = sorted.map(escapeRegExpFragment).join('|');
  if (!pattern) return [{ text: paragraph, bold: false }];
  const re = new RegExp(`(${pattern})`, 'g');
  const parts = paragraph.split(re);
  const boldSet = new Set(sorted);
  const out: { text: string; bold: boolean }[] = [];
  for (const p of parts) {
    if (p === undefined || p === '') continue;
    out.push({ text: p, bold: boldSet.has(p) });
  }
  return out.length ? out : [{ text: paragraph, bold: false }];
}
/** Aviso segments to word tokens. */


function avisoSegmentsToWordTokens(segments: { text: string; bold: boolean }[]): { text: string; bold: boolean }[] {
  const out: { text: string; bold: boolean }[] = [];
  for (const seg of segments) {
    const bits = seg.text.split(/(\s+)/);
    for (const b of bits) {
      if (b !== '') out.push({ text: b, bold: seg.bold });
    }
  }
  return out;
}

export type RenderAvisoParagraphMixedOpts = {
  doc: FoGesrPdfDocument;
  yStart: number;
  margin: number;
  usableW: number;
  pageH: number;
  fontSize: number;
  lineHeight: number;
  paragraph: string;
  onNewPage: () => void;
};
/** Renders aviso paragraph mixed. */


export function renderAvisoParagraphMixed(opts: RenderAvisoParagraphMixedOpts): number {
  const { doc, yStart, margin, usableW, pageH, fontSize, lineHeight, paragraph, onNewPage } = opts;
  const segs = splitAvisoByBoldFragments(paragraph.trim());
  const tokens = avisoSegmentsToWordTokens(segs);
  let y = yStart;
  doc.setFontSize(fontSize);
  const measure = (txt: string, bold: boolean): number => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    return doc.getTextWidth(txt);
  };

  const checkPage = () => {
    if (y + 4 > pageH - margin - 8) {
      onNewPage();
      y = margin;
    }
  };

  let line: { text: string; bold: boolean }[] = [];
  let lw = 0;

  const flush = () => {
    if (line.length === 0) return;
    checkPage();
    let x = margin;
    for (const t of line) {
      doc.setFont('helvetica', t.bold ? 'bold' : 'normal');
      doc.text(t.text, x, y);
      x += measure(t.text, t.bold);
    }
    y += lineHeight;
    line = [];
    lw = 0;
  };

  for (const tok of tokens) {
    const w = measure(tok.text, tok.bold);
    if (lw > 0 && lw + w > usableW) {
      flush();
    }
    if (line.length === 0 && w > usableW) {
      doc.setFont('helvetica', tok.bold ? 'bold' : 'normal');
      const subLines = doc.splitTextToSize(tok.text, usableW);
      for (const sl of subLines) {
        checkPage();
        doc.text(sl, margin, y);
        y += lineHeight;
      }
      continue;
    }
    checkPage();
    line.push(tok);
    lw += w;
  }
  flush();
  doc.setFont('helvetica', 'normal');
  return y;
}
/** Draw page footers (page N of total). */


export function pintarPiePaginas(doc: FoGesrPdfDocument, pageW: number, pageH: number): void {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`-- ${i} of ${total} --`, pageW / 2, pageH - 6, { align: 'center' });
  }
}

/** Copied from `static/brand/` to `dist/brand/` at build → URL `/brand/…`. */
const ALCALDIA_LOGO_PDF_PATH = '/brand/alcaldia-medellin-logo.png';

export type FoGesrHeaderSpec = {
  cod: string;
  version: string;
  centerFirstLine: string;
  centerBodyForWrap: string;
  centerLastLine?: string;
};

export const FO_GESR_SPEC_ENTREGA_BONO: FoGesrHeaderSpec = {
  cod: 'Cód FO-GESR-2160',
  version: 'Versión 2',
  centerFirstLine: 'Formato',
  centerBodyForWrap: 'FO-GESR Entrega de Bono Alimentario y Acompañamiento',
  centerLastLine: 'Educativo',
};

/** Redemption worksheet — official FO-GESR-1775 layout. */
export const FO_GESR_SPEC_REDENCION_BONO: FoGesrHeaderSpec = {
  cod: 'Cód FO-GESR-1775',
  version: 'Versión 5',
  centerFirstLine: 'Formato',
  centerBodyForWrap: 'FO-GESR Planilla de Redención del Bono Alimentario',
};

/**
 * Single FO-GESR-style header row: 20% code/version | 60% title | 20% municipal seal/logo column (bordered table).
 */
export async function drawFoGesrHeaderTable(
  doc: FoGesrPdfDocument,
  y0: number,
  margin: number,
  usableW: number,
  spec: FoGesrHeaderSpec = FO_GESR_SPEC_ENTREGA_BONO,
): Promise<number> {
  const FS_LEFT = 11.5;
  const FS_CENTER = 13.5;

  const pad = 1.2;
  const wL = usableW * 0.2;
  const wC = usableW * 0.6;
  const wR = usableW * 0.2;

  let logoDataUrl: string | null = ALCALDIA_LOGO_PDF_DATA_URL;
  if (!logoDataUrl && globalThis.location !== undefined) {
    const origin = globalThis.location.origin;
    logoDataUrl = await resolveImageSrcForPdf(new URL(ALCALDIA_LOGO_PDF_PATH, origin).href);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FS_CENTER);
  doc.setTextColor(0, 0, 0);
  const maxCW = wC - pad * 3;
  const bodyLines = doc.splitTextToSize(spec.centerBodyForWrap, maxCW);
  const block: string[] = [spec.centerFirstLine, ...bodyLines];
  if (spec.centerLastLine?.trim()) {
    block.push(spec.centerLastLine.trim());
  }
  const lineStepCenter = FS_CENTER * 0.44;
  const totalCenterH = block.length * lineStepCenter;

  let logoNaturalFitH = 0;
  if (logoDataUrl) {
    try {
      const { w: nw, h: nh } = await naturalImageSize(logoDataUrl);
      const fit = fitInBox(nw, nh, wR - pad * 2, 46);
      logoNaturalFitH = fit.dh + pad * 2;
    } catch {
      logoNaturalFitH = 36;
    }
  }

  const minForLeftCells = FS_LEFT * 1.1 * 2;
  let H = Math.max(totalCenterH + pad * 3, logoNaturalFitH, minForLeftCells + 4, 38);
  H = Math.min(H, 52);

  const x0 = margin;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);

  doc.rect(x0, y0, usableW, H);
  doc.line(x0 + wL, y0, x0 + wL, y0 + H);
  doc.line(x0 + wL + wC, y0, x0 + wL + wC, y0 + H);
  doc.line(x0, y0 + H / 2, x0 + wL, y0 + H / 2);

  doc.setFontSize(FS_LEFT);
  const leftBaseline = FS_LEFT * 0.38;
  doc.text(spec.cod, x0 + wL / 2, y0 + H / 4 + leftBaseline, { align: 'center' });
  doc.text(spec.version, x0 + wL / 2, y0 + (3 * H) / 4 + leftBaseline, { align: 'center' });

  const cx = x0 + wL + wC / 2;
  doc.setFontSize(FS_CENTER);
  let ty = y0 + (H - totalCenterH) / 2 + lineStepCenter * 0.72;
  for (const ln of block) {
    doc.text(ln, cx, ty, { align: 'center' });
    ty += lineStepCenter;
  }

  if (logoDataUrl) {
    try {
      const fmt = addImageFormatFromDataUrl(logoDataUrl);
      const { w: nw, h: nh } = await naturalImageSize(logoDataUrl);
      const { dw, dh } = fitInBox(nw, nh, wR - pad * 2, H - pad * 2);
      const rx = x0 + wL + wC + (wR - dw) / 2;
      const ry = y0 + (H - dh) / 2;
      await doc.addImage(logoDataUrl, fmt, rx, ry, dw, dh);
    } catch {
      doc.setFontSize(9);
      doc.text('Alcaldía de Medellín', x0 + wL + wC + wR / 2, y0 + H / 2, { align: 'center' });
    }
  } else {
    doc.setFontSize(9);
    doc.text('Alcaldía de Medellín', x0 + wL + wC + wR / 2, y0 + H / 2, { align: 'center' });
  }

  doc.setFont('helvetica', 'normal');
  return y0 + H + 3;
}
/** Trigger pdf download in browser. */


function triggerPdfDownloadInBrowser(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  queueMicrotask(() => URL.revokeObjectURL(url));
}

/**
 * Browser download path for generated PDF blobs.
 */
export function triggerPdfDownload(blob: Blob, filename: string): void {
  triggerPdfDownloadInBrowser(blob, filename);
}
